// @flow

import { type Amount, type Fx, type Decimal, type CurrencyCode, type LocalTaxCode } from './types';

export type FormatDecimal = (Decimal) => Decimal;

type FormatAmount = (Amount) => string;
type FormatTaxPercentage = (?Decimal) => ?string;
type FormatFx = (Fx) => string;

export function amountToCurrencyString(formatDecimal: FormatDecimal): FormatAmount {
  return (amount: Amount) => {
    return `${getCurrencySymbol(amount[0])} ${formatDecimal(truncateDecimal(amount[1]))}`;
  };
}

export function taxRateToPercentageString(formatDecimal: FormatDecimal): FormatTaxPercentage {
  return (percentage: ?Decimal) => {
    if (percentage === null || percentage === undefined) {
      return null;
    }

    return `${formatDecimal(truncateDecimal(getRatePercentage(percentage)))}`;
  };
}

export function amountToValueString(formatDecimal: FormatDecimal): FormatAmount {
  return (amount: Amount) => {
    return `${formatDecimal(truncateDecimal(amount[1]))}`;
  };
}

export function fxToCurrencyString(formatDecimal: FormatDecimal): FormatFx {
  return (fx: Fx) => {
    return `${getCurrencySymbol(fx.to)} ${formatDecimal(truncateDecimal(fx.rate))}`;
  };
}

export function decimalToNumber(decimal: Decimal): number {
  const number = Number(decimal);

  if (isNaN(number) || parseFloat(decimal) !== number)
    throw new Error('invalid decimal');

  return number;
}

export function getRatePercentage(rate: Decimal): Decimal {
  return `${parseFloat(rate) * 100}`;
}

export function getCurrencySymbol(currencyCode: CurrencyCode): string {
  switch (currencyCode) {
    case 'BRL':
      return 'R$';

    case 'USD':
      return 'US$';

    case 'MXN':
      return 'MX$';

    case 'ARS':
      return '$';

    case 'EUR':
      return '€';

    case 'COP':
      return '$';

    case 'CLP':
      return '$';

    case 'PEN':
      return 'S/.';

    case 'BOB':
      return 'Bs';

    case 'UYU':
      return '$u';

    case 'CRC':
      return '₡';

    case 'PYG':
      return '₲';

    case 'PAB':
      return 'B/.';

    case 'GTQ':
      return 'Q';

    case 'DOP':
      return 'RD$';

    case 'ZAR':
      return 'R';

    case 'KES':
      return 'KSh';

    case 'NGN':
      return '₦';

    case 'INR':
      return '₹';

    case 'TZS':
      return 'TZS';

    case 'UGX':
      return 'UGX';

    case 'ZMW':
      return 'ZMW';

    case 'EGP':
      return 'EGP';

    case 'GHS':
      return 'GHS';

    case 'JMD':
      return 'JM$';

    default:
      throw new Error(`Currency symbol not supported: ${currencyCode}`);
  }
}

export function truncateDecimal(value: Decimal): Decimal {
  return decimalToNumber(value).toFixed(2);
}

export function getLocalTaxCodeFromString(searchString: string): LocalTaxCode {
  switch (searchString.toLowerCase()) {
    case 'iof':
    case 'br_iof':
      return 'BR_IOF';
    case 'iva':
    case 'ar_iva':
      return 'AR_IVA';
    case 'ar_ip':
      return 'AR_IP';
    case 'ig_ibp':
    case 'ar_ig_ibp':
      return 'AR_IG_IBP';
    case 'ar_non_resident_igbp':
      return 'AR_NON_RESIDENT_IGBP';
    case 'ar_iibb':
      return 'AR_IIBB_';
    case 'py_iva':
      return 'PY_IVA';
    case 'cr_iva':
      return 'CR_IVA';
    case 'sv_itbms':
      return 'SV_ITBMS';
    case 'inr_gst':
      return 'INR_GST';

    default:
      throw new Error(`PRS/Money: ${searchString} is not related to any local tax code`);
  }
}
